import { Formik } from 'formik';
import { pick } from 'lodash';
import React, { useMemo, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import Page from 'App/components/Page/Page';
import PatientHeader from 'App/components/PatientHeader';
import TherigyLoadingSpinner from 'App/components/TherigyLoadingSpinner/TherigyLoadingSpinner';
import useFetchActivityDetails from 'App/hooks/useFetchActivityDetails';
import useFetchPatient from 'App/hooks/useFetchPatient';
import { PERMISSION_TYPES } from 'App/utils/isPermitted/isPermitted';
import { TextareaField } from 'Shared/form/TextareaField';
import InformationForm from './components/InformationForm';
import FormButtonGroup from 'App/components/styles/FormButtonGroup';
import { NotFound } from 'App/components/errors';
import useUpdateReferralActivityMutation from 'App/hooks/useUpdateReferralActivityMutation';
import useUpdateReferralActivityMedicationMutation from 'App/hooks/useUpdateReferralActivityMedicationMutation';
import { referralDetailRoute } from 'App/common/routeLookup';
import toast from 'Shared/toast';
import DeleteModal from 'App/components/DeleteModal/DeleteModal';
import useDeleteReferralActivity from 'App/hooks/useDeleteReferralActivity';
import { convertPAtoSave, convertPAtoView } from '../referral.utils';
import { createReferralActivityNote } from 'App/services/RMSService';
import useCreateReferralActivityMutation from 'App/hooks/useCreateReferralActivityMutation';
import BackToLinks from 'App/features/ReferralManager/Referral/BackToLinks';
import EhrEncounterNoteModal from 'App/components/EHR/EhrEncounterNoteModal';
import useFetchEhrBranchSettings from 'App/hooks/useFetchEhrBranchSettings';
import usePatientEhr from 'App/components/PatientProfile/hooks/usePatientEhr';
import PriorAuthorizationEncounterReviewContent from 'App/features/ReferralManager/Referral/PriorAuthorization/components/EncounterModal/PriorAuthorizationModalReview';
import { completedActivityStatusList, STATUS_TYPE } from './constant';
import ReferralActivityHistory from 'App/components/ReferralActivityHistory';
import SubmitButton from 'Shared/form/SubmitButton';
import CommonPatientFieldWrapper from 'App/components/PatientForm/components/CommonPatientFieldWrapper';
import { useUserContext } from 'App/contexts/UserContext';
import EhrProviderNotFoundSection from 'App/components/Assessments/ehr/EhrProviderNotFound';
import { errorHandler } from 'App/utils';

const propTypes = {};
const defaultProps = {};

const validationSchema = yup.object().shape({
    status: yup
        .object()
        .shape({
            id: yup.string().required(),
            name: yup.string().optional(),
        })
        .required(),

    details: yup.object().when('status', {
        is: (status) => {
            return status?.id === STATUS_TYPE.approved;
        },
        then: yup.object().shape({
            paNumber: yup.string().max(64, 'PA Number must be at most 64 characters').nullable(),
            followUpPhone: yup
                .mixed()
                .nullable()
                .test('valid-length', 'Please enter a valid pa follow up phone number.', (value) => {
                    if (value == null || value === '') return true;
                    return /^\d{10}$/.test(String(value));
                }),
            effectiveStartDate: yup.date().required('Please enter an effective start date').nullable(),
            effectiveEndDate: yup.date().required('Please enter an effective end date').nullable(),
            notes: yup.string().max(255).label('Notes').nullable(),
        }),
        otherwise: yup.object().shape({
            paNumber: yup.string().max(64, 'PA Number must be at most 64 characters').nullable(),
            followUpPhone: yup
                .mixed()
                .nullable()
                .notRequired()
                .test('valid-length', 'Please enter a valid pa follow up phone number.', (value) => {
                    if (value == null || value === '') return true;
                    return /^\d{10}$/.test(String(value));
                }),
            effectiveStartDate: yup.date().nullable(),
            effectiveEndDate: yup.date().nullable(),
            notes: yup.string().max(255).label('Notes').nullable(),
        }),
    }),
});

function PriorAuthorization() {
    const { patientId, referralId, priorAuthorizationId, typeId } = useParams();
    const navigate = useNavigate();
    const { isLoading, isError, data: patient } = useFetchPatient(patientId);
    const { data: activityDetails, isLoading: isActivityDetailsLoading } = useFetchActivityDetails(
        patientId,
        referralId,
        priorAuthorizationId,
        typeId
    );
    const { data: branchSettingsData, isLoading: isBranchSettingsLoading } = useFetchEhrBranchSettings();
    const { ehr, isEhrLoading } = usePatientEhr(patientId);
    const user = useUserContext();
    const userEhrEligible = user?.user_attributes?.ehrProviderId;

    let displayEHRNoteModal = false;
    if (!isBranchSettingsLoading && !isEhrLoading) {
        const { redoxRMDestinationId, redoxRMDestinationName, rmNoteDocumentType } = branchSettingsData;
        displayEHRNoteModal =
            ehr?.patientSynced &&
            redoxRMDestinationId &&
            redoxRMDestinationName &&
            rmNoteDocumentType &&
            userEhrEligible;
    }

    const updateReferralActivity = useUpdateReferralActivityMutation();
    const updateReferralActivityMedication = useUpdateReferralActivityMedicationMutation();
    const createReferralActivityMutation = useCreateReferralActivityMutation();
    const navigateToReferralDetailPage = () => {
        navigate(generatePath(referralDetailRoute, { patientId, referralId }));
    };
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const deleteReferralActivity = useDeleteReferralActivity();
    const [ehrModalOpen, setEhrModalOpen] = useState(false);

    const deleteActivity = () => {
        deleteReferralActivity
            .mutateAsync({
                patientId,
                referralId,
                activityId: priorAuthorizationId,
            })
            .then(() => {
                navigateToReferralDetailPage();
            })
            .catch(errorHandler);
    };

    const initialValues = useMemo(() => {
        return {
            disableActivity: false,
            disableInputs: false,
            ...(activityDetails ? convertPAtoView(activityDetails) : {}),
        };
    }, [activityDetails]);

    if (isLoading) {
        return <TherigyLoadingSpinner />;
    }

    if (isError) {
        return (
            <Page title="Not Found" showPageTitle={false} permissionType={PERMISSION_TYPES.EVERYONE}>
                <NotFound />
            </Page>
        );
    }

    const pageTitle = typeId === '4' ? 'Prior Authorization Renewal' : 'Prior Authorization';

    const openEhrEncounterNoteModal = () => {
        setEhrModalOpen(true);
    };

    return (
        <Page title={pageTitle} permissionType={PERMISSION_TYPES.ENABLE_REFERRAL}>
            <div className="container page-wrapper">
                <Row>
                    <Col md={12}>
                        <PatientHeader patient={patient} showEhrStatus={false} />
                    </Col>
                </Row>

                <BackToLinks patientId={patientId} referralId={referralId} />

                {displayEHRNoteModal && (
                    <div>
                        <ReferralActivityHistory activityHistory={activityDetails?.history} />
                    </div>
                )}

                <Formik
                    initialValues={initialValues}
                    enableReinitialize
                    validationSchema={validationSchema}
                    onSubmit={(values) => {
                        const { medicationPayload, activityPayload, notesPayload, appealActivityPayload } =
                            convertPAtoSave(values);
                        const promiseArray = [
                            updateReferralActivity.mutateAsync({
                                patientId,
                                referralId,
                                activityId: priorAuthorizationId,
                                data: activityPayload,
                            }),
                        ];
                        medicationPayload.forEach((med) => {
                            promiseArray.push(
                                updateReferralActivityMedication.mutateAsync({
                                    patientId,
                                    referralId,
                                    activityId: priorAuthorizationId,
                                    medicationId: med.id,
                                    data: pick(med, ['medication_id', 'data']),
                                })
                            );
                        });
                        if (appealActivityPayload) {
                            promiseArray.push(
                                createReferralActivityMutation.mutateAsync({
                                    patientId,
                                    referralId,
                                    body: appealActivityPayload,
                                })
                            );
                        }
                        promiseArray.push(createReferralActivityNote(priorAuthorizationId, notesPayload));

                        Promise.all(promiseArray).then(() => {
                            toast.success('Activity Information has been updated successfully');
                            if (displayEHRNoteModal) {
                                openEhrEncounterNoteModal();
                            } else {
                                navigateToReferralDetailPage();
                            }
                        });
                    }}
                >
                    {({ isSubmitting, handleSubmit, values }) => {
                        const disableSendToEhrButton =
                            Array.isArray(activityDetails?.history) &&
                            activityDetails.history.some((item) =>
                                completedActivityStatusList.includes(item.referralActivityStatus)
                            );

                        return (
                            <form onSubmit={handleSubmit} noValidate>
                                <InformationForm />
                                <Row>
                                    <CommonPatientFieldWrapper fieldName="referralPaNotes">
                                        {(commonProps) => (
                                            <Col md={6}>
                                                <TextareaField
                                                    {...commonProps}
                                                    fieldName="details.notes"
                                                    label="Notes"
                                                    placeholder="Notes"
                                                    disabled={
                                                        commonProps.disabled ||
                                                        values?.disableActivity ||
                                                        values?.disableInputs
                                                    }
                                                    rows={3}
                                                />
                                            </Col>
                                        )}
                                    </CommonPatientFieldWrapper>
                                </Row>
                                {!userEhrEligible && !isEhrLoading && ehr?.patientSynced && (
                                    <Row>
                                        <Col md={9}>
                                            <EhrProviderNotFoundSection />
                                        </Col>
                                    </Row>
                                )}
                                <FormButtonGroup>
                                    <Button
                                        variant="danger"
                                        onClick={() => setShowDeleteModal(true)}
                                        disabled={isSubmitting || values.disableActivity || values.disableInputs}
                                    >
                                        Delete
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        type="button"
                                        disabled={isSubmitting}
                                        onClick={navigateToReferralDetailPage}
                                    >
                                        Cancel
                                    </Button>
                                    <SubmitButton disabled={values?.disableActivity || values?.disableInputs}>
                                        Save Activity
                                    </SubmitButton>
                                    {displayEHRNoteModal &&
                                        completedActivityStatusList.includes(initialValues.status?.id) && (
                                            <Button
                                                disabled={isSubmitting || disableSendToEhrButton}
                                                variant="outline-secondary"
                                                onClick={openEhrEncounterNoteModal}
                                            >
                                                Send To EHR
                                            </Button>
                                        )}
                                </FormButtonGroup>

                                {ehrModalOpen &&
                                    !isActivityDetailsLoading &&
                                    !isBranchSettingsLoading &&
                                    !isEhrLoading &&
                                    values.status?.id && (
                                        <EhrEncounterNoteModal
                                            activity={{ ...activityDetails }}
                                            patient={patient}
                                            branchSettings={branchSettingsData}
                                            activityStatus={values.status.id}
                                            onComplete={() => {
                                                return navigateToReferralDetailPage();
                                            }}
                                        >
                                            <PriorAuthorizationEncounterReviewContent
                                                status={values.status.id}
                                                patient={patient}
                                                branchSettings={branchSettingsData}
                                                medicationList={values.medications}
                                                priorAuthorizationReferralDetails={{ ...values.details }}
                                            />
                                        </EhrEncounterNoteModal>
                                    )}
                            </form>
                        );
                    }}
                </Formik>
            </div>
            {showDeleteModal && (
                <DeleteModal
                    onHide={() => setShowDeleteModal(false)}
                    show={showDeleteModal}
                    onDelete={deleteActivity}
                    title="Delete Activity"
                    message="Are you sure you want to delete this activity?"
                />
            )}
        </Page>
    );
}

PriorAuthorization.propTypes = propTypes;
PriorAuthorization.defaultProps = defaultProps;

export default PriorAuthorization;
